import { useEffect, useMemo, useState } from 'react';
import { SystemContextLabel } from '@/components/SystemContextLabel';
import { TickBadgeIcon, UploadIcon } from '@/components/icons';
import { APP_BRAND } from '@/config/brand';
import { BulkImportCredentialIcons } from './BulkImportCredentialIcons';
import { BulkImportEditableCell } from './BulkImportEditableCell';
import { BulkIssueMobileCredentialModal } from './BulkIssueAllMobileModal';
import type { BulkCredentialIssuePhase } from './BulkCredentialIssueFollowUp';
import {
  countMobileCodeSentRows,
  getBulkUserPostImportStatus,
} from '@/utils/bulkUserPostImport';
import type { BulkUserRow } from '@/types/bulkUserImport';
import { downloadBulkUserCsv } from '@/utils/downloadBulkUserCsv';

export type { BulkCredentialIssuePhase } from './BulkCredentialIssueFollowUp';

export type BulkImportTableMode = 'review' | 'post-import';

type BulkUserRowPatch = Partial<
  Pick<
    BulkUserRow,
    'firstName' | 'lastName' | 'email' | 'cardNumber' | 'hasMobile' | 'hasPin' | 'mobileCredentialIssued'
  >
>;

interface BulkImportUsersTableProps {
  rows: BulkUserRow[];
  fileName: string | null;
  systemLabel: string;
  mode?: BulkImportTableMode;
  readOnly?: boolean;
  credentialIssuePhase?: BulkCredentialIssuePhase;
  credentialToastCount?: number | null;
  onDismissCredentialToast?: () => void;
  onIssueAllMobile?: () => void;
  onIssueMobile?: (row: BulkUserRow) => void;
  onUploadNewFile?: () => void;
  onRowUpdate?: (rowId: string, patch: BulkUserRowPatch) => void;
  onApprove?: () => void;
  approveDisabled?: boolean;
  showApprove?: boolean;
}

function BulkImportCheckbox({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      className={`bulk-import__checkbox${checked ? ' bulk-import__checkbox--checked' : ''}`}
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
    >
      {checked ? (
        APP_BRAND === 'hid' ? (
          <TickBadgeIcon size={18} />
        ) : (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden>
            <path
              d="M1 4L3.5 6.5L9 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )
      ) : null}
    </button>
  );
}

function FooterSpinner() {
  return (
    <svg className="bulk-import__footer-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.25" />
      <path d="M14 8a6 6 0 00-6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PostImportRowSkeleton() {
  return (
    <>
      <td className="bulk-import__data-cell bulk-import__data-cell--credentials">
        <span className="bulk-import__row-skeleton bulk-import__row-skeleton--icon" />
      </td>
      <td className="bulk-import__data-cell bulk-import__data-cell--status">
        <span className="bulk-import__row-skeleton bulk-import__row-skeleton--badge" />
      </td>
      <td className="bulk-import__data-cell bulk-import__data-cell--action">
        <span className="bulk-import__row-skeleton bulk-import__row-skeleton--btn" />
      </td>
    </>
  );
}

function SearchIcon() {
  return (
    <svg
      className="bulk-import__data-search-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.25" />
      <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M6.5 2.5H3.75A1.25 1.25 0 002.5 3.75v8.5A1.25 1.25 0 003.75 13.25h8.5a1.25 1.25 0 001.25-1.25V9.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 2.5h4.5V7M13.5 2.5L7 9"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BulkImportUsersTable({
  rows,
  fileName,
  systemLabel,
  mode = 'review',
  readOnly = false,
  credentialIssuePhase = 'idle',
  credentialToastCount = null,
  onDismissCredentialToast,
  onIssueAllMobile,
  onIssueMobile,
  onUploadNewFile,
  onRowUpdate,
  onApprove,
  approveDisabled = false,
  showApprove = false,
}: BulkImportUsersTableProps) {
  const [query, setQuery] = useState('');
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueTarget, setIssueTarget] = useState<BulkUserRow | null>(null);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = `${row.firstName} ${row.lastName} ${row.email}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [query, rows]);

  const uploadedLabel = useMemo(() => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  }, []);

  const pageSizeLabel = 10;
  const isPostImport = mode === 'post-import';
  const mobileCodeSentCount = useMemo(() => countMobileCodeSentRows(filteredRows), [filteredRows]);

  useEffect(() => {
    if (credentialToastCount == null || !onDismissCredentialToast) return;
    const timer = window.setTimeout(onDismissCredentialToast, 5000);
    return () => window.clearTimeout(timer);
  }, [credentialToastCount, onDismissCredentialToast]);

  const handleIssueAllClick = () => {
    if (mobileCodeSentCount === 0) return;
    setIssueTarget(null);
    setIssueModalOpen(true);
  };

  const handleIssueRowClick = (row: BulkUserRow) => {
    setIssueTarget(row);
    setIssueModalOpen(true);
  };

  const handleIssueConfirm = () => {
    setIssueModalOpen(false);
    if (issueTarget) {
      onIssueMobile?.(issueTarget);
    } else {
      onIssueAllMobile?.();
    }
    setIssueTarget(null);
  };

  const handleIssueCancel = () => {
    setIssueModalOpen(false);
    setIssueTarget(null);
  };

  const handleExport = () => {
    downloadBulkUserCsv(rows, fileName);
  };

  const renderStatusBadge = (row: BulkUserRow) => {
    if (isPostImport) {
      const postStatus = getBulkUserPostImportStatus(row);
      return (
        <span
          className={`bulk-import__status-badge bulk-import__status-badge--${postStatus === 'code-sent' ? 'code-sent' : 'credential-issued'}`}
        >
          {postStatus === 'code-sent' ? 'Code sent' : 'Credential issued'}
        </span>
      );
    }

    return (
      <span className="bulk-import__status-badge bulk-import__status-badge--ready">Ready to add</span>
    );
  };

  const renderActionCell = (row: BulkUserRow) => {
    if (!isPostImport) return null;

    if (getBulkUserPostImportStatus(row) === 'code-sent') {
      return (
        <button
          type="button"
          className="bulk-import__issue-btn"
          onClick={() => handleIssueRowClick(row)}
        >
          Issue credential
        </button>
      );
    }

    return <span className="bulk-import__no-action">No action</span>;
  };

  return (
    <div className="bulk-import__data">
      <div className="bulk-import__topbar">
        <header className="bulk-import__data-header">
          <div className="bulk-import__data-header-left">
            <h2 className="bulk-import__data-title">Bulk add users</h2>
            {fileName ? (
              <p className="bulk-import__data-subline">Source: {fileName}</p>
            ) : null}
          </div>
          <div className="bulk-import__data-header-right">
            <div className="bulk-import__data-meta">
              <SystemContextLabel
                label={systemLabel}
                className="bulk-import__data-meta-row bulk-import__data-meta-row--system"
                iconVariant="plain"
                iconSize={18}
              />
              <span className="bulk-import__data-subline bulk-import__data-meta-row bulk-import__data-meta-row--uploaded">
                Uploaded at: {uploadedLabel}
              </span>
            </div>
          </div>
        </header>

        <div className="bulk-import__data-toolbar">
          <div className="bulk-import__data-toolbar-main">
            <div className="bulk-import__data-search-wrap">
              <SearchIcon />
              {!query ? (
                <span className="bulk-import__data-search-placeholder" aria-hidden>
                  Search by name, email
                </span>
              ) : null}
              <input
                type="text"
                className="bulk-import__data-search"
                placeholder=""
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="bulk-user-filter"
                aria-label="Search by name or email"
              />
            </div>
            <p className="bulk-import__data-count">Total people: {filteredRows.length}</p>
          </div>
          <div className="bulk-import__data-toolbar-actions">
            {onUploadNewFile ? (
              <button type="button" className="bulk-import__data-outline-btn" onClick={onUploadNewFile}>
                <UploadIcon className="bulk-import__data-btn-icon" />
                Upload a new file
              </button>
            ) : null}
            <button
              type="button"
              className="bulk-import__data-outline-btn"
              onClick={handleExport}
              disabled={rows.length === 0}
            >
              <ExportIcon />
              Export
            </button>
          </div>
        </div>
      </div>

      <div className="bulk-import__data-body">
        {credentialToastCount != null ? (
          <div className="bulk-import__issue-toast-banner" role="status" aria-live="polite">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <circle cx="8" cy="8" r="8" fill="var(--color-success)" />
              <path
                d="M5 8.5L7 10.5L11 6.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              Credentials issued for {credentialToastCount}{' '}
              mobile {credentialToastCount === 1 ? 'user' : 'users'}.
            </span>
            {onDismissCredentialToast ? (
              <button
                type="button"
                className="bulk-import__issue-toast-dismiss"
                aria-label="Dismiss notification"
                onClick={onDismissCredentialToast}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path
                    d="M3 3l8 8M11 3l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="bulk-import__data-table-wrap">
          <table className="bulk-import__data-table">
            <thead>
              <tr>
                <th scope="col" className="bulk-import__data-th bulk-import__data-th--num">
                  S&nbsp;no
                </th>
                <th scope="col" className="bulk-import__data-th">
                  First name
                </th>
                <th scope="col" className="bulk-import__data-th">
                  Last name
                </th>
                <th scope="col" className="bulk-import__data-th">
                  Email
                </th>
                {isPostImport ? (
                  <>
                    <th scope="col" className="bulk-import__data-th">
                      Credentials
                    </th>
                    <th scope="col" className="bulk-import__data-th bulk-import__data-th--status">
                      Status
                    </th>
                    <th scope="col" className="bulk-import__data-th bulk-import__data-th--action">
                      Action
                    </th>
                  </>
                ) : (
                  <>
                    <th scope="col" className="bulk-import__data-th bulk-import__data-th--center">
                      Mobile
                    </th>
                    <th scope="col" className="bulk-import__data-th bulk-import__data-th--center">
                      PIN
                    </th>
                    <th scope="col" className="bulk-import__data-th">
                      Card
                    </th>
                    <th scope="col" className="bulk-import__data-th bulk-import__data-th--status">
                      Status
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, index) => (
                <tr key={row.id}>
                  <td className="bulk-import__data-cell bulk-import__data-cell--num">{index + 1}.</td>
                  <td className="bulk-import__data-cell">
                    <BulkImportEditableCell
                      value={row.firstName}
                      readOnly={readOnly}
                      onCommit={(firstName) => onRowUpdate?.(row.id, { firstName })}
                    />
                  </td>
                  <td className="bulk-import__data-cell">
                    <BulkImportEditableCell
                      value={row.lastName}
                      readOnly={readOnly}
                      onCommit={(lastName) => onRowUpdate?.(row.id, { lastName })}
                    />
                  </td>
                  <td className="bulk-import__data-cell bulk-import__data-cell--email">
                    <BulkImportEditableCell
                      value={row.email}
                      readOnly={readOnly}
                      inputType="email"
                      onCommit={(email) => onRowUpdate?.(row.id, { email })}
                    />
                  </td>
                  {isPostImport ? (
                    <>
                      {credentialIssuePhase === 'issuing' &&
                      getBulkUserPostImportStatus(row) === 'code-sent' ? (
                        <PostImportRowSkeleton />
                      ) : (
                        <>
                          <td className="bulk-import__data-cell bulk-import__data-cell--credentials">
                            <BulkImportCredentialIcons
                              hasMobile={row.hasMobile}
                              hasPin={row.hasPin}
                              cardNumber={row.cardNumber}
                            />
                          </td>
                          <td className="bulk-import__data-cell bulk-import__data-cell--status">
                            {renderStatusBadge(row)}
                          </td>
                          <td className="bulk-import__data-cell bulk-import__data-cell--action">
                            {renderActionCell(row)}
                          </td>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <td className="bulk-import__data-cell bulk-import__data-cell--check">
                        <BulkImportCheckbox
                          checked={row.hasMobile ?? false}
                          disabled={readOnly}
                          label={`Mobile for ${row.firstName} ${row.lastName}`}
                          onChange={() =>
                            onRowUpdate?.(row.id, { hasMobile: !(row.hasMobile ?? false) })
                          }
                        />
                      </td>
                      <td className="bulk-import__data-cell bulk-import__data-cell--check">
                        <BulkImportCheckbox
                          checked={row.hasPin ?? false}
                          disabled={readOnly}
                          label={`PIN for ${row.firstName} ${row.lastName}`}
                          onChange={() =>
                            onRowUpdate?.(row.id, { hasPin: !(row.hasPin ?? false) })
                          }
                        />
                      </td>
                      <td className="bulk-import__data-cell">
                        <BulkImportEditableCell
                          value={row.cardNumber ?? ''}
                          readOnly={readOnly}
                          placeholder="No card"
                          mutedWhenEmpty
                          onCommit={(cardNumber) =>
                            onRowUpdate?.(row.id, { cardNumber: cardNumber || undefined })
                          }
                        />
                      </td>
                      <td className="bulk-import__data-cell bulk-import__data-cell--status">
                        {renderStatusBadge(row)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="bulk-import__data-footer">
        <button type="button" className="bulk-import__data-page-size" aria-label="Rows per page">
          {pageSizeLabel} per page
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden>
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
          </svg>
        </button>
        <div className="bulk-import__data-pagination">
          <button type="button" className="bulk-import__data-page-nav" disabled aria-label="Previous page">
            &lt; Prev
          </button>
          <span className="bulk-import__data-page-current">
            <strong>1</strong>/1
          </span>
          <button type="button" className="bulk-import__data-page-nav" disabled aria-label="Next page">
            Next &gt;
          </button>
        </div>
        {showApprove ? (
          <button
            type="button"
            className="bulk-import__approve-btn"
            disabled={approveDisabled}
            onClick={onApprove}
          >
            Add {filteredRows.length} {filteredRows.length === 1 ? 'user' : 'users'}
          </button>
        ) : isPostImport && mobileCodeSentCount > 0 ? (
          <button
            type="button"
            className={`bulk-import__approve-btn${credentialIssuePhase === 'preparing' || credentialIssuePhase === 'issuing' ? ' bulk-import__approve-btn--loading' : ''}`}
            disabled={credentialIssuePhase === 'preparing' || credentialIssuePhase === 'issuing'}
            onClick={handleIssueAllClick}
          >
            {credentialIssuePhase === 'preparing' || credentialIssuePhase === 'issuing' ? (
              <>
                <FooterSpinner />
                Issuing credentials
              </>
            ) : mobileCodeSentCount === 1 ? (
              'Issue credential for 1 mobile user'
            ) : (
              `Issue credential for ${mobileCodeSentCount} mobile users`
            )}
          </button>
        ) : (
          <span aria-hidden />
        )}
      </footer>

      <BulkIssueMobileCredentialModal
        open={issueModalOpen}
        mobileCount={mobileCodeSentCount}
        issueTarget={issueTarget}
        onCancel={handleIssueCancel}
        onConfirm={handleIssueConfirm}
      />
    </div>
  );
}
